import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { SpinnerGapIcon, PlayIcon } from '@phosphor-icons/react';
import { handleDownload, getMediaType, formatTimeIndex } from '@/shared/functions';
import Controls from './controls';
import Track from './track';
import Transcript from './transcript';
import { MediaObject, Source, SourceObject } from '@/shared/types';
import { AuthContext, FeaturesContext } from '@/app/contexts';
interface TranscriptItem {
  start: number;
  end: number;
  text: string;
}
interface MediaPlayerProps {
  mediaItem: MediaObject;
  transcriptSrc?: string;
  showTranscript?: boolean;
  showTranscriptMarkers?: boolean;
  transcriptPosition?: 'side' | 'below';
  disableHotkeys?: boolean;
  updateMediaContext?: (field: string | object, value?: any) => void;
  useStillFrame?: boolean;
  isAccordion?: boolean;
  isTable?: boolean;
  isEditing?: boolean;
  isUnifiedSources?: boolean;
  isFullscreen?: boolean;
  isPresentation?: boolean;
  temporaryRotation?: any;
  sourcesExpanded?: boolean;
}
const getSecondsFromTimeString = (timeString: string | number): number => {
  // If it's already a number, return it
  if (typeof timeString === 'number') {
    return timeString;
  }
  const timeStringArray = timeString.split(':');
  if (timeStringArray.length === 1) {
    return Number(timeString);
  } else if (timeStringArray.length === 2) {
    const [minutes, seconds] = timeStringArray.map(Number);
    return minutes * 60 + seconds;
  } else if (timeStringArray.length === 3) {
    const [hours, minutes, seconds] = timeStringArray.map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  }
  return 0;
};
const MediaPlayer: React.FC<MediaPlayerProps> = ({
  mediaItem,
  transcriptSrc,
  showTranscript = true,
  showTranscriptMarkers = false,
  transcriptPosition: providedTranscriptPosition,
  disableHotkeys,
  updateMediaContext,
  useStillFrame,
  isAccordion,
  isTable,
  isEditing = false,
  isUnifiedSources,
  isFullscreen = false,
  isPresentation = false,
  temporaryRotation,
  sourcesExpanded
}) => {
  const mediaRef = useRef<HTMLAudioElement | HTMLVideoElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [activeSegmentIndex, setActiveSegmentIndex] = useState<number>(-1);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [usableMediaSrc, setUsableMediaSrc] = useState<string | null>(null);
  const [showTranscriptState, setShowTranscriptState] = useState(showTranscript);
  const [showTranscriptMarkersState, setShowTranscriptMarkersState] = useState(showTranscriptMarkers);
  const [transcriptText, setTranscriptText] = useState<string | undefined>(undefined);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const menuRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredTranscript, setFilteredTranscript] = useState<TranscriptItem[]>([]);
  const [isSearchInputVisible, setIsSearchInputVisible] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const playerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditingMediaContext, setIsEditingMediaContext] = useState(false);
  const [hasManuallyOverriddenEndTime, setHasManuallyOverriddenEndTime] = useState(false);
  const isAudioFile = getMediaType(mediaItem, mediaItem.sourceInfo || {}) === 'audio';
  const defaultTranscriptPosition = isAudioFile ? 'side' : 'side';
  const [isPortrait, setIsPortrait] = useState(false);
  const [transcriptPosition, setTranscriptPosition] = useState<'side' | 'below'>(providedTranscriptPosition || defaultTranscriptPosition);
  const [videoStyle, setVideoStyle] = useState({
    width: '100%',
    height: '100%',
    rotate: `${mediaItem?.mediaContext?.rotation}deg`
  });
  const [videoCentererStyle, setVideoCentererStyle] = useState({
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  });
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0
  });
  const [totalRotation, setTotalRotation] = useState<number | undefined>(mediaItem?.mediaContext?.rotation);
  const [stillFrameCentererStyle, setStillFrameCentererStyle] = useState({
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  });
  const rectRef = useRef<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoCentererRef = useRef<HTMLDivElement | null>(null);
  const stillFrameCentererRef = useRef<HTMLDivElement | null>(null);
  const dimensionsLockRef = useRef(false);
  const stillFrameRef = useRef<HTMLImageElement | null>(null);
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  const handleDownloadWithIntercomFlag = useCallback((source: SourceObject | Source) => handleDownload(source, !!featureFlags?.useIntercom), [featureFlags?.useIntercom]);
  useEffect(() => {
    let newTotalRotation = (temporaryRotation || 0) + (mediaItem?.mediaContext?.rotation || 0);
    if (newTotalRotation > 270) {
      newTotalRotation = 0;
    } else if (newTotalRotation < 0) {
      newTotalRotation = 270;
    }
    setTotalRotation(newTotalRotation);
  }, [temporaryRotation, mediaItem?.mediaContext?.rotation, mediaItem?.mediaContext?.lastUpdated]);
  useEffect(() => {
    const setUsableMediaSrcWithConversionCheck = async () => {
      if (mediaItem) {
        let srcToUse = mediaItem?.mediaContext?.previewUrl || mediaItem?.mediaSrc || mediaItem?.mediaUrl || '';
        if (mediaItem?.sourceInfo?.previewObjectUrl) {
          srcToUse = `THE_SOURCE${mediaItem?.sourceInfo?.previewObjectUrl}`;
        }
        if (useStillFrame && mediaItem?.mediaContext?.stillFrame && mediaItem?.mediaType === 'video') {
          setUsableMediaSrc(`/api/proxy/${encodeURIComponent(srcToUse)}#t=${mediaItem?.mediaContext?.stillFrame}`);
        } else if (!isEditingMediaContext && (mediaItem?.mediaContext?.startTime || mediaItem?.mediaContext?.endTime === 0) && (mediaItem?.mediaType === 'video' || mediaItem?.mediaType === 'audio')) {
          const startTimeString = mediaItem?.mediaContext?.startTime ? `${mediaItem?.mediaContext?.startTime}` : '0';
          const endTimeString = mediaItem?.mediaContext?.endTime ? mediaItem?.mediaContext?.endTime : undefined;
          const startTimeNumber = startTimeString ? getSecondsFromTimeString(startTimeString) : 0;
          const endTimeNumber = endTimeString ? getSecondsFromTimeString(endTimeString) : undefined;
          const startTimeSeconds = !Number.isNaN(startTimeNumber) ? startTimeNumber : '0';
          const endTimeSeconds = endTimeNumber && !Number.isNaN(endTimeNumber) && endTimeNumber > 0 ? endTimeNumber : undefined;
          setUsableMediaSrc(`/api/proxy/${encodeURIComponent(srcToUse)}${startTimeSeconds ? `#t=${startTimeSeconds}` : '#t=0'}${endTimeSeconds ? `,${endTimeSeconds}` : ''}`);
        } else {
          setUsableMediaSrc(`/api/proxy/${encodeURIComponent(srcToUse)}`);
        }
      }
    };
    setUsableMediaSrcWithConversionCheck();
  }, [mediaItem, useStillFrame]);
  useEffect(() => {
    if (mediaItem?.mediaContext?.showTranscript !== undefined) {
      setShowTranscriptState(mediaItem.mediaContext.showTranscript);
    }
    if (mediaItem?.mediaContext?.showTranscriptMarkers !== undefined && searchQuery.trim() === '') {
      setShowTranscriptMarkersState(mediaItem.mediaContext.showTranscriptMarkers);
    }
    if (isFullscreen && mediaItem?.mediaType === 'video') {
      setTranscriptPosition(providedTranscriptPosition || 'side');
    } else if (mediaItem?.mediaContext?.transcriptPosition !== undefined) {
      setTranscriptPosition(mediaItem.mediaContext.transcriptPosition);
    }
    if (mediaItem?.mediaContext?.autoScroll !== undefined) {
      setAutoScroll(mediaItem.mediaContext.autoScroll);
    } else {
      if (updateMediaContext) {
        updateMediaContext('autoScroll', true);
      }
    }
    if (mediaItem?.mediaContext?.rotation !== undefined) {
      setTotalRotation(mediaItem?.mediaContext?.rotation || 0);
    }

    // Reset override state when media item changes
    setHasManuallyOverriddenEndTime(false);
  }, [mediaItem?.mediaContext?.transcriptPosition, mediaItem?.mediaContext?.showTranscript, mediaItem?.mediaContext?.showTranscriptMarkers, mediaItem?.mediaContext?.autoScroll, mediaItem?.mediaContext?.startTime, mediaItem?.mediaContext?.endTime, mediaItem?.mediaType, isFullscreen]);
  useEffect(() => {
    if (searchQuery.trim() !== '') {
      if (!showTranscriptMarkersState) {
        setShowTranscriptMarkersState(true);
        if (updateMediaContext) {
          updateMediaContext('showTranscriptMarkers', true);
        }
      }
    } else {
      if (showTranscriptMarkersState !== showTranscriptMarkers) {
        setShowTranscriptMarkersState(showTranscriptMarkers);
        if (updateMediaContext) {
          updateMediaContext('showTranscriptMarkers', showTranscriptMarkers);
        }
      }
    }
  }, [searchQuery]);
  const processTranscript = (data: any): TranscriptItem[] => {
    const transcriptItems = data.map((transcript: any) => {
      const startInSeconds = getSecondsFromTimeString(transcript.start);
      const endInSeconds = getSecondsFromTimeString(transcript.end);
      const text = transcript.segment ? transcript.segment.trim() : transcript.text ? transcript.text.trim() : '';
      return {
        speaker: transcript.speaker || undefined,
        start: startInSeconds,
        end: endInSeconds,
        text: text
      };
    });
    return transcriptItems;
  };
  useEffect(() => {
    const loadTranscript = async () => {
      if (!transcriptSrc || transcriptSrc.trim() === '' || !featureFlags?.orgId) return;
      try {
        let response = null;
        let dataText = undefined;
        let apiUrl = `/api/proxy/${encodeURIComponent(transcriptSrc)}`;
        response = await fetch(apiUrl, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`
          },
          method: 'GET'
        });
        const responseForTextTest = response.clone();
        try {
          const data = await response.json();
          if ((data as any).transcript) {
            const processedTranscript = processTranscript((data as any).transcript);
            setTranscript(processedTranscript);
            setTranscriptText(undefined);
          } else {
            const responseText = await responseForTextTest.text();
            dataText = responseText;
            setTranscriptText(dataText);
          }
        } catch (error) {
          const responseText = await responseForTextTest.text();
          dataText = responseText;
          setTranscriptText(dataText);
        }
      } catch (error) {
        console.error('Error loading transcript:', error);
        setTranscript([]);
        setTranscriptText(undefined);
      }
    };
    if (!useStillFrame && featureFlags?.orgId) {
      loadTranscript();
    }
  }, [transcriptSrc, useStillFrame]);
  useEffect(() => {
    if (transcript.length > 0) {
      setActiveSegmentIndex(0); // Initialize to the first segment
    }
  }, [transcript]);
  const handleLoadedMetadata = () => {
    if (!!mediaRef.current && mediaItem.mediaType === 'video') {
      const video = mediaRef.current as HTMLVideoElement;
      if (!rectRef.current) {
        rectRef.current = video.getBoundingClientRect();
      }
      const swapWidthAndHeight = totalRotation === 90 || totalRotation === -90 || totalRotation === 270;
      const videoRatio = swapWidthAndHeight ? video.videoHeight / video.videoWidth : video.videoWidth / video.videoHeight;
      let maxWidth = containerDimensions.width;
      let maxHeight = containerDimensions.height;
      if ((!maxWidth || maxWidth <= 0) && containerRef.current) {
        const ancestor = containerRef.current.closest('.mediaDisplay') as HTMLElement || containerRef.current.parentElement as HTMLElement || containerRef.current;
        if (ancestor) {
          const rect = ancestor.getBoundingClientRect();
          maxWidth = Math.round(rect.width);
          if (isFullscreen) {
            maxHeight = Math.round(rect.height - 160);
          } else if (isPresentation) {
            maxHeight = Math.round(rect.height - 128);
          } else {
            const topOffset = rect.top > 0 ? rect.top : 0;
            maxHeight = Math.round(window.innerHeight - topOffset - 160);
            if (maxHeight <= 0) maxHeight = Math.round(window.innerHeight * 0.6);
          }
        }
      }
      const hasTranscript = showTranscriptState && transcript && transcript.length > 0;
      if (hasTranscript) {
        if (transcriptPosition === 'side') {
          maxWidth = maxWidth - 266;
        } else {
          maxHeight = maxHeight - 300;
        }
      }
      if (!maxHeight || maxHeight <= 0 || Number.isNaN(maxHeight)) {
        const naturalHeight = swapWidthAndHeight ? 100 : maxWidth / videoRatio;
        const viewportCap = window.innerHeight * 0.7;
        maxHeight = Math.min(naturalHeight, viewportCap);
      }
      if (!isFullscreen && !isTable && !isAccordion && !isUnifiedSources) {
        maxHeight = Math.min(maxHeight, 400);
      }
      const naturalWidth = swapWidthAndHeight ? video.videoHeight : video.videoWidth;
      const naturalHeight = swapWidthAndHeight ? video.videoWidth : video.videoHeight;
      if (!isFullscreen && naturalWidth > 0 && naturalHeight > 0) {
        maxWidth = Math.min(maxWidth, naturalWidth);
        maxHeight = Math.min(maxHeight, naturalHeight);
      }
      const maxRatio = maxWidth / maxHeight;
      const limitingFactor = videoRatio > maxRatio ? 'width' : 'height';
      setIsPortrait(videoRatio < 1);
      let centererWidth = 0;
      let centererHeight = 0;
      let widthToUse = 0;
      let heightToUse = 0;
      if (limitingFactor === 'width') {
        widthToUse = swapWidthAndHeight ? 0 : maxWidth;
        heightToUse = swapWidthAndHeight ? maxWidth : 0;
        centererWidth = maxWidth;
        centererHeight = swapWidthAndHeight ? maxWidth / videoRatio : maxWidth / videoRatio;
      } else {
        widthToUse = swapWidthAndHeight ? maxHeight : 0;
        heightToUse = swapWidthAndHeight ? 0 : maxHeight;
        centererWidth = swapWidthAndHeight ? maxHeight * videoRatio : maxHeight * videoRatio;
        centererHeight = maxHeight;
      }
      centererWidth = Math.min(centererWidth, maxWidth);
      centererHeight = Math.min(centererHeight, maxHeight);
      if (videoCentererRef.current) {
        setVideoCentererStyle({
          width: `${centererWidth}px`,
          height: `${centererHeight}px`,
          overflow: 'hidden'
        });
      }
      setVideoStyle({
        rotate: `${totalRotation}deg`,
        width: widthToUse ? `${widthToUse}px` : 'auto',
        height: heightToUse ? `${heightToUse}px` : 'auto'
      });
    }
  };
  useEffect(() => {
    handleLoadedMetadata();
  }, [totalRotation, containerDimensions.width, containerDimensions.height, showTranscriptState, transcriptPosition, sourcesExpanded]);
  useEffect(() => {
    if (mediaItem.mediaType === 'video' && !isTable && !isAccordion && !isUnifiedSources) {
      requestAnimationFrame(() => {
        dimensionsLockRef.current = false;
        updateContainerDimensions();
      });
    }
  }, [sourcesExpanded]);
  const updateContainerDimensions = () => {
    if (!containerRef.current || mediaItem.mediaType !== 'video') return;
    if (dimensionsLockRef.current) return;
    dimensionsLockRef.current = true;
    const ancestor = containerRef.current.closest('.mediaDisplay') as HTMLElement || containerRef.current.parentElement as HTMLElement || containerRef.current;
    const ancestorRect = ancestor.getBoundingClientRect();
    const adjustedWidth = Math.round(ancestorRect.width);
    let adjustedHeight: number;
    if (isFullscreen) {
      adjustedHeight = Math.round(ancestorRect.height - 160);
    } else if (isPresentation) {
      adjustedHeight = Math.round(ancestorRect.height - 128);
      if (sourcesExpanded) {
        adjustedHeight = Math.round(adjustedHeight * 0.8);
      }
    } else {
      const topOffset = ancestorRect.top > 0 ? ancestorRect.top : 0;
      adjustedHeight = Math.round(window.innerHeight - topOffset - 160);
      if (adjustedHeight <= 0) adjustedHeight = Math.round(window.innerHeight * 0.6);
    }
    setContainerDimensions({
      width: adjustedWidth,
      height: adjustedHeight
    });
    requestAnimationFrame(() => {
      dimensionsLockRef.current = false;
    });
  };
  useEffect(() => {
    if (mediaItem.mediaType !== 'video' || isTable || isAccordion || isUnifiedSources) return;
    let debounceId: NodeJS.Timeout;
    const onResize = () => {
      clearTimeout(debounceId);
      dimensionsLockRef.current = false;
      debounceId = setTimeout(updateContainerDimensions, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(debounceId);
      window.removeEventListener('resize', onResize);
    };
  }, [mediaItem, isTable, isAccordion, isUnifiedSources, isPresentation, isFullscreen]);
  useEffect(() => {
    if (mediaItem.mediaType !== 'video' || isTable || isAccordion || isUnifiedSources) return;
    const ancestor = containerRef.current?.closest('.mediaDisplay') as HTMLElement | null;
    if (!ancestor) return;
    let debounceId: NodeJS.Timeout;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceId);
      dimensionsLockRef.current = false;
      debounceId = setTimeout(updateContainerDimensions, 200);
    });
    observer.observe(ancestor);
    return () => {
      clearTimeout(debounceId);
      observer.disconnect();
    };
  }, [containerDimensions.width, mediaItem, isTable, isAccordion, isUnifiedSources]);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const setAudioData = () => {
      setDuration(media.duration);
    };
    const setAudioTime = () => {
      window.dispatchEvent(new Event('tp-media-playing'));
      setCurrentTime(media.currentTime);

      // Only pause at end point if playback started before the end point and user hasn't manually overridden
      if (!isEditingMediaContext && mediaItem?.mediaContext?.endTime && !hasManuallyOverriddenEndTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (media.currentTime >= endTimeSeconds) {
          media.pause();
          setIsPlaying(false);
        }
      }
    };

    // Events
    media.addEventListener('loadedmetadata', setAudioData);
    media.addEventListener('timeupdate', setAudioTime);
    media.addEventListener('ended', () => {
      if (!isEditingMediaContext && mediaItem?.mediaContext?.endTime && !hasManuallyOverriddenEndTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (media.currentTime >= endTimeSeconds) {
          setIsPlaying(false);
        }
      }
    });
    return () => {
      media.removeEventListener('loadedmetadata', setAudioData);
      media.removeEventListener('timeupdate', setAudioTime);
      media.removeEventListener('ended', () => {});
    };
  }, [usableMediaSrc, isEditingMediaContext, mediaItem?.mediaContext?.endTime, hasManuallyOverriddenEndTime]);

  // Reset override state when entering/exiting edit mode
  useEffect(() => {
    setHasManuallyOverriddenEndTime(false);
  }, [isEditingMediaContext]);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    const updateCurrentTime = () => {
      setCurrentTime(media.currentTime);
      requestAnimationFrame(updateCurrentTime);
    };
    requestAnimationFrame(updateCurrentTime);
    return () => {};
  }, [playbackRate]);
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredTranscript(transcript);
    } else {
      const lowerCaseQuery = searchQuery.toLowerCase();
      const filtered = transcript.filter(item => item.text.toLowerCase().includes(lowerCaseQuery));
      setFilteredTranscript(filtered);
    }
  }, [searchQuery, transcript]);
  useEffect(() => {
    // Update active segment based on current time
    const newActiveIndex = filteredTranscript.findIndex(item => currentTime >= item.start && currentTime <= item.end);
    if (newActiveIndex !== activeSegmentIndex) {
      setActiveSegmentIndex(newActiveIndex);
    }
  }, [currentTime, filteredTranscript, activeSegmentIndex]);
  useEffect(() => {
    if (disableHotkeys) return;
    let scrubbingInterval: NodeJS.Timeout | null = null;
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore hotkeys if any input element or markdown editor has focus
      if (isEditingMediaContext || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement || event.target instanceof HTMLElement && event.target.closest('.mdxeditor-root-contenteditable') || event.target instanceof HTMLElement && event.target.closest('#feedback-button')) {
        return;
      }
      if (event.shiftKey && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        event.preventDefault();
        event.stopPropagation();
        const scrubAmount = 1; // Scrub distance in seconds

        if (scrubbingInterval) return;
        scrubbingInterval = setInterval(() => {
          if (mediaRef.current) {
            if (event.key === 'ArrowRight') {
              mediaRef.current.currentTime = Math.min(mediaRef.current.currentTime + scrubAmount, duration);
            } else if (event.key === 'ArrowLeft') {
              mediaRef.current.currentTime = Math.max(mediaRef.current.currentTime - scrubAmount, 0);
            }
            setCurrentTime(mediaRef.current.currentTime);
          }
        }, 200); // Scrub every 200ms for smoother effect
      } else if (!event.shiftKey) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveToNextSegment();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveToPreviousSegment();
        }
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (scrubbingInterval && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
        clearInterval(scrubbingInterval);
        scrubbingInterval = null;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (scrubbingInterval) {
        clearInterval(scrubbingInterval);
      }
    };
  }, [duration, filteredTranscript, activeSegmentIndex, disableHotkeys]);
  const moveToNextSegment = () => {
    let nextIndex = filteredTranscript.findIndex((item, index) => index > activeSegmentIndex && item.start > currentTime);
    if (nextIndex === -1 && activeSegmentIndex === -1) {
      nextIndex = filteredTranscript.findIndex(item => item.start > currentTime);
    }
    if (nextIndex !== -1) {
      setActiveSegmentIndex(nextIndex);
      handleSegmentClick(filteredTranscript[nextIndex].start);
      if (transcriptRef.current) {
        const el = transcriptRef.current.children[nextIndex] as HTMLElement;
        if (el) {
          const container = transcriptRef.current;
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
            container.scrollTo({
              top: el.offsetTop - container.offsetTop - container.clientHeight / 2 + el.clientHeight / 2,
              behavior: 'smooth'
            });
          }
        }
      }
    } else if (activeSegmentIndex === filteredTranscript.length - 1) {
      if (mediaRef.current) {
        mediaRef.current.currentTime = duration;
        setCurrentTime(duration);
      }
    }
  };
  const moveToPreviousSegment = () => {
    let prevIndex = -1;
    if (activeSegmentIndex !== -1) {
      prevIndex = filteredTranscript.slice(0, activeSegmentIndex).reverse().findIndex(item => item.end < currentTime);
      if (prevIndex !== -1) {
        prevIndex = activeSegmentIndex - 1 - prevIndex;
      }
    } else {
      prevIndex = filteredTranscript.slice().reverse().findIndex(item => item.end < currentTime);
      if (prevIndex !== -1) {
        prevIndex = filteredTranscript.length - 1 - prevIndex;
      }
    }
    if (prevIndex !== -1) {
      setActiveSegmentIndex(prevIndex);
      handleSegmentClick(filteredTranscript[prevIndex].start);
      if (transcriptRef.current) {
        const el = transcriptRef.current.children[prevIndex] as HTMLElement;
        if (el) {
          const container = transcriptRef.current;
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
            container.scrollTo({
              top: el.offsetTop - container.offsetTop - container.clientHeight / 2 + el.clientHeight / 2,
              behavior: 'smooth'
            });
          }
        }
      }
    } else if (activeSegmentIndex === 0) {
      if (mediaRef.current) {
        mediaRef.current.currentTime = 0;
        setCurrentTime(0);
      }
    }
  };
  const togglePlay = () => {
    if (!mediaRef.current) return;
    if (isPlaying) {
      mediaRef.current.pause();
    } else {
      // If in edit mode, start from the start time indicator position
      if (isEditingMediaContext && mediaItem?.mediaContext?.startTime) {
        const startTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.startTime);
        mediaRef.current.currentTime = startTimeSeconds;
        setCurrentTime(startTimeSeconds);
      }

      // If user is clicking play while at or past the end time, allow them to continue past it
      if (!isEditingMediaContext && mediaItem?.mediaContext?.endTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (mediaRef.current.currentTime >= endTimeSeconds) {
          setHasManuallyOverriddenEndTime(true);
        }
      }

      // If current time is before the end point, playback will stop at end point
      // If current time is after the end point, playback will continue normally
      mediaRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const toggleMute = () => {
    if (!mediaRef.current) return;
    if (isMuted) {
      mediaRef.current.volume = volume;
    } else {
      mediaRef.current.volume = 0;
    }
    setIsMuted(!isMuted);
  };
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current) return;
    const time = parseFloat(e.target.value);
    mediaRef.current.currentTime = time;
    setCurrentTime(time);

    // Reset override state if seeking before end time
    if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
      const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
      if (time < endTimeSeconds) {
        setHasManuallyOverriddenEndTime(false);
      }
    }
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!mediaRef.current) return;
    const newVolume = parseFloat(e.target.value);
    mediaRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };
  const handleSegmentClick = (startTime: number) => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = startTime;
      setCurrentTime(startTime);

      // Reset override state if seeking before end time
      if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
        const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
        if (startTime < endTimeSeconds) {
          setHasManuallyOverriddenEndTime(false);
        }
      }
    }
  };
  const handlePlaybackRateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newRate = parseFloat(e.target.value);
    setPlaybackRate(newRate);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = newRate;
    }
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mediaRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width * 100;
    const newTime = percentage / 100 * duration;
    setHoverTime(newTime);
  };
  const handleMouseLeave = () => {
    setHoverTime(null);
  };
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>, startTime?: number, updateMediaContext?: (field: string, value: any) => void, mediaItem?: any) => {
    if (!mediaRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width * 100;
    const newTime = percentage / 100 * duration;

    // If in edit mode and clicking to the left of the start indicator, move the start indicator
    if (isEditingMediaContext && startTime !== undefined && newTime < startTime) {
      if (updateMediaContext) {
        updateMediaContext('startTime', newTime.toFixed(0));
      }
      return;
    }
    mediaRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    // Reset override state if seeking before end time
    if (hasManuallyOverriddenEndTime && mediaItem?.mediaContext?.endTime) {
      const endTimeSeconds = getSecondsFromTimeString(mediaItem.mediaContext.endTime);
      if (newTime < endTimeSeconds) {
        setHasManuallyOverriddenEndTime(false);
      }
    }
  };
  const toggleTranscriptMarkersVisibility = () => {
    const newState = !showTranscriptMarkersState;
    setShowTranscriptMarkersState(newState);
    if (updateMediaContext) {
      updateMediaContext('showTranscriptMarkers', newState);
    }
  };
  const toggleTranscriptVisibility = () => {
    const newState = !showTranscriptState;
    setShowTranscriptState(newState);
    if (updateMediaContext) {
      updateMediaContext('showTranscript', newState);
    }
  };
  const setStillFrame = () => {
    if (updateMediaContext && mediaRef.current) {
      const stillFrameTime = mediaRef.current.currentTime.toFixed(0);
      updateMediaContext('stillFrame', stillFrameTime);
      const width = (mediaRef.current as HTMLVideoElement).videoWidth;
      const height = (mediaRef.current as HTMLVideoElement).videoHeight;
      const aspectRatio = width / height;
      const canvasWidth = 120;
      const canvasHeight = canvasWidth / aspectRatio;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      ctx?.drawImage(mediaRef.current as HTMLVideoElement, 0, 0, canvasWidth, canvasHeight);
      const image = canvas.toDataURL('image/webp');
      updateMediaContext({
        'stillFrame': stillFrameTime,
        'canvas': {
          'stillFrameImage': image,
          'stillFrame': stillFrameTime
        }
      });
    }
  };
  const handleSetStartTime = () => {
    if (mediaItem?.mediaContext && updateMediaContext) {
      const newStartTime = currentTime;
      const newEndTime = mediaItem.mediaContext.endTime;
      updateMediaContext('startTime', newStartTime);
      updateMediaContext('endTime', newEndTime);
    }
  };
  const handleSetEndTime = () => {
    if (mediaItem?.mediaContext && updateMediaContext) {
      const newStartTime = mediaItem.mediaContext.startTime;
      const newEndTime = currentTime;
      updateMediaContext('startTime', newStartTime);
      updateMediaContext('endTime', newEndTime);
    }
  };
  const handleUpdateMediaContext = (field: string, value: any) => {
    if (mediaItem?.mediaContext && updateMediaContext) {
      updateMediaContext(field, value);
    }
  };
  const toggleMenuVisibility = () => {
    setIsMenuVisible(!isMenuVisible);
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuVisible(false);
      }
    };
    if (!isTable && !isAccordion && !isUnifiedSources) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTable, isAccordion, isUnifiedSources]);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchQuery = e.target.value;
    setSearchQuery(newSearchQuery);

    // If search is being cleared and transcripts were initially hidden
    if (newSearchQuery === '' && !showTranscript) {
      setShowTranscriptState(false);
      if (updateMediaContext) {
        updateMediaContext('showTranscript', false);
      }
    }
  };
  const toggleSearchInputVisibility = () => {
    setIsSearchInputVisible(prevState => !prevState);
  };
  useEffect(() => {
    if (isSearchInputVisible && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchInputVisible]);
  const changeTranscriptPosition = (position: 'side' | 'below') => {
    setTranscriptPosition(position);
    if (updateMediaContext) {
      updateMediaContext('transcriptPosition', position);
    }
  };

  // Function to get the current height of the player
  const getPlayerHeight = () => {
    if (playerRef.current) {
      return playerRef.current.clientHeight;
    }
    return 0;
  };

  // Function to get the current width of the player
  const getPlayerWidth = () => {
    if (playerRef.current) {
      return playerRef.current.clientWidth;
    }
    return 0;
  };
  const handleLoadStart = () => {
    setIsLoading(true);
  };
  const handleLoadedData = () => {
    setIsLoading(false);
  };
  const handleMediaTagError = async (e: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>) => {
    const target = e.currentTarget;
    const error = target.error;

    // Get detailed error information
    const errorDetails = {
      code: error?.code,
      message: error?.message,
      errorName: error ? ['MEDIA_ERR_ABORTED', 'MEDIA_ERR_NETWORK', 'MEDIA_ERR_DECODE', 'MEDIA_ERR_SRC_NOT_SUPPORTED'][error.code - 1] : 'Unknown',
      src: target.src,
      networkState: target.networkState,
      readyState: target.readyState,
      currentSrc: target.currentSrc
    };
    console.error('🎥 Media Error:', errorDetails);
    console.error('Full error object:', error);
    console.error('Target element:', target);
  };
  useEffect(() => {
    if (transcriptPosition === 'below' && transcriptRef.current) {
      const playerWidth = getPlayerWidth();
      transcriptRef.current.style.width = `${playerWidth}px`;
    }
  }, [transcriptPosition, getPlayerWidth]);
  const toggleAutoScroll = () => {
    setAutoScroll(!autoScroll);
    if (updateMediaContext) {
      updateMediaContext('autoScroll', !autoScroll);
    }
  };
  const checkStillFrameSize = () => {
    if (stillFrameRef.current) {
      const stillFrame = stillFrameRef.current as HTMLImageElement;
      const width = stillFrame.clientWidth;
      const height = stillFrame.clientHeight;
      if (totalRotation === 90 || totalRotation === -90 || totalRotation === 270) {
        setStillFrameCentererStyle({
          width: `${height}px`,
          height: `${width}px`,
          overflow: 'hidden'
        });
      }
    }
  };
  const isGoingToRenderSetStillFramePrompt = !isAudioFile && (isUnifiedSources || isTable) && !mediaItem.mediaContext?.canvas?.stillFrameImage && !isAccordion;
  if (!usableMediaSrc) return <div className="flex w-full max-w-2xl space-y-4 mt-3 items-center justify-center">
      <div className="flex flex-row items-center justify-center gap-2">
        <SpinnerGapIcon size={25} className='animate-spin-slow animate-pulse' />
        <span className='text-medium animate-pulse'>Loading media</span>
      </div>
    </div>;
  return <div ref={containerRef} data-testid="media-player-component" className={`flex gap-4 responsive-flex box-border h-auto ${transcriptPosition === 'below' ? `flex-col justify-start ${isAudioFile ? 'items-stretch' : 'items-center'}` : 'items-start justify-center flex-row'}`} data-sentry-component="MediaPlayer" data-sentry-source-file="player.tsx">
      <div ref={playerRef} className={`box-border relative flex flex-col p-0 ${isGoingToRenderSetStillFramePrompt || isFullscreen ? isAudioFile ? 'pt-[18px]' : 'pt-0' : 'pt-4'}
          ${isTable ? '' : 'rounded-t-none rounded-b-lg'}
          ${isTable || isAccordion ? '' : 'bg-black/50 border-1 border-b-3 border-gray-700 border-solid'}
          ${isFullscreen ? `max-h-[calc(100vh-35px)] ${isAudioFile ? 'w-full' : 'w-max'}` : 'w-full h-full'}
        `}>
        {isAudioFile ? <>
            {(isUnifiedSources || isAccordion || isTable) && !isFullscreen ? <div className="flex flex-col items-center justify-center">
                <PlayIcon size={25} />
              </div> : <audio ref={mediaRef as React.RefObject<HTMLAudioElement>} src={usableMediaSrc} className="rounded-md" onLoadStart={handleLoadStart} onLoadedData={handleLoadedData} onError={handleMediaTagError} controls={false} style={{
          height: '100%',
          width: '100%'
        }} />}
          </> : <>
            {isUnifiedSources || isAccordion || isTable ? <>
                {mediaItem.mediaContext?.canvas?.stillFrameImage ? <div ref={stillFrameCentererRef} className={`flex flex-row items-center justify-center overflow-hidden object-contain
                       ${isTable ? 'h-84 w-114 max-h-84 max-w-114' : ''}
                       ${isUnifiedSources ? 'h-120 w-120 max-h-120 max-w-120' : ''}
                    `}>
                    <img ref={stillFrameRef} src={mediaItem.mediaContext.canvas.stillFrameImage} onLoad={e => {
              checkStillFrameSize();
            }} height={isTable ? 84 : isUnifiedSources ? 120 : undefined} width={isTable || isUnifiedSources ? 'auto' : undefined} alt="Still frame" className="rounded-md" style={{
              transform: `rotate(${totalRotation}deg)`
            }} />
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-row items-center justify-center text-white">
                      <PlayIcon size={20} weight="fill" />
                    </div>
                  </div> : <div className="flex flex-col items-center justify-center">
                    <PlayIcon size={25} />
                    {!isAccordion && <span className="text-xs text-center whitespace-normal">Click &ldquo;Set Still Frame&rdquo; to create thumbnail.</span>}
                  </div>}
              </> : <>
                {isLoading && <div className="flex flex-col absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 items-center justify-center w-full">
                    <div className={`
                      text-sm animate-pulse text-center
                      ${isAccordion || isTable ? 'text-xs' : ''}
                    `}>Loading media...
                    </div>
                  </div>}
                <div ref={videoCentererRef} className="flex flex-row items-center justify-center self-center" style={videoCentererStyle}>
                  <video className="rounded-md" style={videoStyle} ref={mediaRef as React.RefObject<HTMLVideoElement>} src={usableMediaSrc} onLoadStart={handleLoadStart} onLoadedData={handleLoadedData} onLoadedMetadata={handleLoadedMetadata} onError={handleMediaTagError} controls={false}>
                    <source src={usableMediaSrc} />
                  </video>
                </div>
              </>}
          </>}
        {!useStillFrame && !isUnifiedSources && <Track currentTime={currentTime} duration={duration} handleTimeChange={handleTimeChange} handleTimelineClick={handleTimelineClick} handleMouseMove={handleMouseMove} handleMouseLeave={handleMouseLeave} hoverTime={hoverTime} filteredTranscript={filteredTranscript} activeSegmentIndex={activeSegmentIndex} handleSegmentClick={handleSegmentClick} showTranscriptMarkers={showTranscriptMarkersState} startTime={mediaItem?.mediaContext?.startTime ? getSecondsFromTimeString(mediaItem.mediaContext.startTime) : undefined} endTime={mediaItem?.mediaContext?.endTime && mediaItem?.mediaContext?.endTime !== '0' ? getSecondsFromTimeString(mediaItem.mediaContext.endTime) : undefined} updateMediaContext={handleUpdateMediaContext} mediaItem={mediaItem} isPlaying={isPlaying} isEditingMediaContext={isEditingMediaContext} />}
        {!useStillFrame && !isUnifiedSources && <>
            <Controls isPlaying={isPlaying} togglePlay={togglePlay} isMuted={isMuted} toggleMute={toggleMute} volume={volume} handleVolumeChange={handleVolumeChange} currentTime={formatTimeIndex(currentTime.toString()) || '00:00'} duration={duration} isMenuVisible={isMenuVisible} toggleMenuVisibility={toggleMenuVisibility} playbackRate={playbackRate} handlePlaybackRateChange={handlePlaybackRateChange} showTranscript={showTranscriptState} toggleTranscriptVisibility={toggleTranscriptVisibility} showTranscriptMarkers={showTranscriptMarkersState} toggleTranscriptMarkersVisibility={toggleTranscriptMarkersVisibility} transcriptText={transcriptText} transcriptAvailable={transcript && transcript.length > 0} menuRef={menuRef} changeTranscriptPosition={changeTranscriptPosition} currentPosition={transcriptPosition} transcriptSrc={transcriptSrc} setStillFrame={setStillFrame} setStartTime={handleSetStartTime} setEndTime={handleSetEndTime} mediaItem={mediaItem} autoScroll={autoScroll} toggleAutoScroll={toggleAutoScroll} updateMediaContext={updateMediaContext || (() => {})} setIsEditingMediaContext={setIsEditingMediaContext} isEditingMediaContext={isEditingMediaContext} isEditing={isEditing} />
          </>}
      </div>
      {transcript && transcript.length > 0 && transcriptSrc && showTranscriptState && !useStillFrame && !isUnifiedSources && <Transcript mediaItem={mediaItem} position={transcriptPosition} isPortrait={isPortrait} playerHeight={getPlayerHeight()} playerWidth={getPlayerWidth()} transcriptRef={transcriptRef} transcript={transcript} filteredTranscript={filteredTranscript} activeSegmentIndex={activeSegmentIndex} handleSegmentClick={handleSegmentClick} searchQuery={searchQuery} handleSearchChange={handleSearchChange} isSearchInputVisible={isSearchInputVisible} toggleSearchInputVisibility={toggleSearchInputVisibility} searchInputRef={searchInputRef} transcriptSrc={transcriptSrc} handleDownload={handleDownloadWithIntercomFlag} style={{
      width: transcriptPosition === 'below' ? isAudioFile ? '100%' : `${getPlayerWidth()}px` : 'auto'
    }} autoScroll={autoScroll} />}
    </div>;
};
export default MediaPlayer;